import { memo, useMemo, useRef, useCallback, useEffect } from "react";
import { LegendList } from "@legendapp/list/react";
import type { LegendListRef } from "@legendapp/list/react";
import type { RefObject, ReactNode } from "react";
import type { WebSearchCitation } from "@shared/schema";
import type { SourceContext } from "@/services/contextOrchestrator";
import { countMessageContentLines, getMessageContentLength } from "@/lib/chatHistoryLimits";

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string | unknown;
  timestamp: Date;
  citations?: WebSearchCitation[];
  contextCitations?: Record<number, SourceContext>;
  images?: Array<{ url: string; detail?: string }>;
  model?: string;
  autoSelected?: boolean;
};

type ListRow =
  | { rowType: "message"; id: string; message: Message }
  | { rowType: "typing"; id: "__typing__" }
  | { rowType: "research"; id: "__research__" }
  | { rowType: "end"; id: "__end__" };

/** LegendList `refScrollView` forwards an imperative handle; parent may still want the scrollable DOM. */
function resolveScrollContainerDom(node: unknown): HTMLDivElement | null {
  if (node == null) return null;
  if (typeof (node as HTMLElement).addEventListener === "function") {
    return node as HTMLDivElement;
  }
  const api = node as { getScrollableNode?: () => HTMLElement | null };
  if (typeof api.getScrollableNode === "function") {
    const el = api.getScrollableNode();
    if (el && typeof el.addEventListener === "function") {
      return el as HTMLDivElement;
    }
  }
  return null;
}

export type ChatMessageListRef = LegendListRef;

interface VirtualizedMessageListProps {
  messages: Message[];
  renderMessage: (message: Message) => ReactNode;
  typingIndicator?: ReactNode;
  researchIndicator?: ReactNode;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  /** Optional scrollable DOM node (legacy); prefer listRef for scroll control. */
  containerRef?: RefObject<HTMLDivElement | null>;
  /** Imperative LegendList API (scrollToEnd, getState, …). */
  listRef?: RefObject<ChatMessageListRef | null>;
  /** True when the viewport is near/at the end of the list. */
  onNearEndChange?: (nearEnd: boolean) => void;
}

/** Matches MessageBubble collapsed preview threshold */
const LONG_MESSAGE_CHAR_THRESHOLD = 4000;
const MAX_MEASURED_ITEM_HEIGHT = 12_000;
const COLLAPSED_MESSAGE_ESTIMATE = 380;

export const VirtualizedMessageList = memo(function VirtualizedMessageList({
  messages,
  renderMessage,
  typingIndicator,
  researchIndicator,
  messagesEndRef,
  containerRef,
  listRef,
  onNearEndChange,
}: VirtualizedMessageListProps) {
  const measuredHeights = useRef<Map<string, number>>(new Map());
  const contentLengthByIdRef = useRef<Map<string, number>>(new Map());
  const nearEndUnsubsRef = useRef<Array<() => void>>([]);
  const onNearEndChangeRef = useRef(onNearEndChange);
  onNearEndChangeRef.current = onNearEndChange;

  // Invalidate only messages whose content length changed (avoids full-map thrash while streaming).
  useEffect(() => {
    const prev = contentLengthByIdRef.current;
    const next = new Map<string, number>();
    for (const m of messages) {
      const len = getMessageContentLength(m.content);
      next.set(m.id, len);
      if (prev.get(m.id) !== len) {
        measuredHeights.current.delete(m.id);
      }
    }
    for (const id of prev.keys()) {
      if (!next.has(id)) {
        measuredHeights.current.delete(id);
      }
    }
    contentLengthByIdRef.current = next;
  }, [messages]);

  useEffect(() => {
    return () => {
      for (const unsub of nearEndUnsubsRef.current) unsub();
      nearEndUnsubsRef.current = [];
    };
  }, []);

  const data = useMemo<ListRow[]>(() => {
    const list: ListRow[] = messages.map((message) => ({
      rowType: "message" as const,
      id: message.id,
      message,
    }));

    if (typingIndicator) {
      list.push({ rowType: "typing", id: "__typing__" });
    }

    if (researchIndicator) {
      list.push({ rowType: "research", id: "__research__" });
    }

    list.push({ rowType: "end", id: "__end__" });
    return list;
  }, [messages, typingIndicator, researchIndicator]);

  const getEstimatedItemSize = useCallback((item: ListRow, index: number, _type: string | undefined) => {
    void index;
    void _type;
    if (item.rowType === "typing") return 80;
    if (item.rowType === "research") return 120;
    if (item.rowType === "end") return 1;

    const cached = measuredHeights.current.get(item.id);
    if (cached) return cached;

    const rawContent = item.message.content;
    const contentLength = getMessageContentLength(rawContent);
    const hasCitations = (item.message.citations?.length ?? 0) > 0;
    const hasImages = (item.message.images?.length ?? 0) > 0;

    if (contentLength > LONG_MESSAGE_CHAR_THRESHOLD) {
      let estimate = COLLAPSED_MESSAGE_ESTIMATE;
      if (hasCitations) estimate += 150;
      if (hasImages) estimate += 200;
      return estimate;
    }

    const lineCount = countMessageContentLines(rawContent);
    const lineBasedEstimate = 120 + lineCount * 22;
    const charBasedEstimate = 120 + Math.min(contentLength * 0.5, 8000);
    let estimate = Math.min(Math.max(lineBasedEstimate, charBasedEstimate), 8000);
    if (hasCitations) estimate += 150;
    if (hasImages) estimate += 200;
    return estimate;
  }, []);

  const assignListRef = useCallback(
    (node: LegendListRef | null) => {
      for (const unsub of nearEndUnsubsRef.current) unsub();
      nearEndUnsubsRef.current = [];

      if (listRef) {
        (listRef as React.MutableRefObject<LegendListRef | null>).current = node;
      }

      if (!node) return;

      const state = node.getState();
      if (!state?.listen) return;

      const emit = () => {
        const nearEnd =
          state.isNearEnd ||
          state.isAtEnd ||
          state.isWithinMaintainScrollAtEndThreshold;
        onNearEndChangeRef.current?.(nearEnd);
      };

      nearEndUnsubsRef.current = [
        state.listen("isNearEnd", emit),
        state.listen("isAtEnd", emit),
        state.listen("isWithinMaintainScrollAtEndThreshold", emit),
      ];
      emit();
    },
    [listRef]
  );

  const refScrollView = useCallback(
    (node: unknown) => {
      if (!containerRef) return;
      const assign = () => {
        const dom = resolveScrollContainerDom(node);
        // eslint-disable-next-line react-hooks/immutability -- ref forwarding; mutating .current is intended
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = dom;
      };
      if (node == null) {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = null;
        return;
      }
      assign();
      if (resolveScrollContainerDom(node) === null) {
        requestAnimationFrame(assign);
      }
    },
    [containerRef]
  );

  const onItemSizeChanged = useCallback(
    (info: { size: number; itemData: ListRow }) => {
      const row = info.itemData;
      if (row.rowType === "message") {
        const clamped = Math.min(info.size, MAX_MEASURED_ITEM_HEIGHT);
        measuredHeights.current.set(row.id, clamped);
      }
    },
    []
  );

  return (
    <LegendList<ListRow>
      ref={assignListRef}
      refScrollView={containerRef ? refScrollView : undefined}
      data={data}
      keyExtractor={(item) => item.id}
      getItemType={(item) => item.rowType}
      getEstimatedItemSize={getEstimatedItemSize}
      onItemSizeChanged={onItemSizeChanged}
      recycleItems={false}
      estimatedItemSize={200}
      drawDistance={800}
      initialScrollAtEnd
      maintainScrollAtEnd={{
        on: { dataChange: true, layout: true, itemLayout: true },
      }}
      maintainScrollAtEndThreshold={0.15}
      maintainVisibleContentPosition
      className="h-full overflow-y-auto overflow-x-hidden relative overscroll-contain"
      style={{ willChange: "transform" }}
      contentContainerClassName="pr-4"
      renderItem={({ item }) => {
        if (item.rowType === "message") {
          return <>{renderMessage(item.message)}</>;
        }
        if (item.rowType === "typing") {
          return <>{typingIndicator}</>;
        }
        if (item.rowType === "research") {
          return <>{researchIndicator}</>;
        }
        return <div ref={messagesEndRef} />;
      }}
    />
  );
});
